import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography, CircularProgress, LinearProgress } from '@mui/material';
import { storage } from '../../firebase';
import { getDownloadURL, listAll, ref, uploadBytesResumable } from 'firebase/storage';

interface MediaLibraryDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (item: { url: string; name: string }) => void;
}

type MediaItem = { url?: string; name: string; loading?: boolean; progress?: number; tempId?: string };

export default function MediaLibraryDialog({ open, onClose, onSelect }: MediaLibraryDialogProps) {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(false); // 予備フラグ（将来の全体ローディング表示に利用可能）
  const [file, setFile] = useState<File | null>(null);
  const [keyword, setKeyword] = useState('');
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const baseRef = ref(storage, 'media');
      const res = await listAll(baseRef);
      setTotalCount(res.items.length);
      // 先に枠（ローディング）を表示
      setItems(res.items.map((it) => ({ name: it.name, loading: true })));
      // 各アイテムURLを個別取得して差し替え
      res.items.forEach(async (itemRef) => {
        try {
          const url = await getDownloadURL(itemRef);
          setItems((prev) => prev.map((it) => it.name === itemRef.name ? { ...it, url, loading: false } : it));
        } catch (e) {
          console.error('Failed to get URL:', e);
          setItems((prev) => prev.map((it) => it.name === itemRef.name ? { ...it, loading: false } : it));
        }
      });
    } catch (e: any) {
      console.error('Failed to list media:', e);
      const message = String(e?.message || e);
      if (message.includes('storage/unauthorized')) {
        alert('メディアの読み込み権限がありません。Firebase Storage ルールで /media/** を許可してください。');
      } else if (message.includes('storage/object-not-found')) {
        // フォルダ未作成でもOKなので黙って空表示
        setItems([]);
        setTotalCount(0);
      } else {
        alert('メディアの取得に失敗しました: ' + message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadAll();
  }, [open]);

  const handleUpload = async () => {
    if (!file) return;
    try {
      const path = `media/${Date.now()}_${file.name}`;
      const objectRef = ref(storage, path);
      // プレースホルダーを先に表示
      const tempId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      setItems((prev) => [{ name: file.name, loading: true, progress: 0, tempId }, ...prev]);
      const task = uploadBytesResumable(objectRef, file);
      task.on('state_changed', (snapshot) => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setItems((prev) => prev.map((it) => it.tempId === tempId ? { ...it, progress } : it));
      }, (error) => {
        console.error('Upload failed:', error);
        setItems((prev) => prev.filter((it) => it.tempId !== tempId));
        const errorMessage = (error as Error).message;
        if (errorMessage.includes('storage/unauthorized')) {
          alert('アップロード権限がありません。Firebase ConsoleでStorageルールを確認してください。');
        } else {
          alert('アップロードに失敗しました: ' + errorMessage);
        }
      }, async () => {
        const url = await getDownloadURL(objectRef);
        setItems((prev) => prev.map((it) => it.tempId === tempId ? { name: file.name, url, loading: false, progress: 100 } : it));
        setFile(null);
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      });
    } catch (error) {
      console.error('Upload failed:', error);
      alert('アップロードに失敗しました');
    }
  };

  const filtered = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    if (!k) return items;
    return items.filter((i) => i.name.toLowerCase().includes(k) || (i.url || '').toLowerCase().includes(k));
  }, [items, keyword]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>メディアライブラリ</DialogTitle>
      <DialogContent sx={{ minHeight: '60vh', maxHeight: '70vh', overflow: 'auto' }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <Button variant="outlined" disabled={!file} onClick={handleUpload}>アップロード</Button>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
            {totalCount === null ? '件数取得中…' : `合計 ${totalCount} 件`}
          </Typography>
          <TextField size="small" placeholder="検索" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
        </Box>

        {loading && (
          <Box sx={{ mb: 1 }}>
            <LinearProgress />
          </Box>
        )}

        <Grid container spacing={2}>
          {filtered.map((it) => (
            <Grid item xs={6} sm={4} md={3} key={(it.url || it.tempId || it.name)}>
              <Box
                sx={{ border: '1px solid #eee', borderRadius: 1, p: 1, cursor: 'pointer' }}
                onClick={() => it.url && onSelect({ url: it.url, name: it.name })}
              >
                <Box sx={{ position: 'relative', width: '100%', height: 120, borderRadius: 1, overflow: 'hidden', bgcolor: '#f3f3f3' }}>
                  {it.url ? (
                    <img src={it.url} alt={it.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <CircularProgress size={28} />
                    </Box>
                  )}
                </Box>
                {typeof it.progress === 'number' && it.progress >= 0 && it.progress < 100 && (
                  <Box sx={{ mt: 0.5 }}>
                    <LinearProgress variant="determinate" value={it.progress} />
                  </Box>
                )}
                <Box sx={{ fontSize: 12, mt: 0.5, wordBreak: 'break-all' }}>{it.name}</Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
}


